/**
 * 🏢 JEFE DEPARTAMENTO SERVICE
 *
 * P13-V7B — WHAT THIS SERVICE IS NOW. Three readers, each composed prefix-scoped like the rest of
 * the application (the routed caller of the first is `/incidents`; the other two are read by the
 * department-head stores only):
 *
 *   · `getIncidencesByDepartment` → `GET /department-head/incidences`  (department_head, super_admin)
 *   · `getUserMemberships`        → `GET /memberships/user/:id`        (super_admin, group_manager)
 *   · `getCurrentBackendUser`     → `GET /auth/validate`
 *
 * WHAT LEFT, AND WHY. The service used to carry eighteen more methods built on three base paths
 * that began with `/api/` — under a client whose base is already `/api`, so every one of them
 * composed `/api/api/...` and answered 404. They are not repaired, because repairing a prefix is
 * only right when somebody is entitled to the answer:
 *
 *   · `getTeamMetrics` / `getTeamMembers` and `getAccessibleDepartments` were requested on every
 *     mount of `/incidents` for a department head. Their canonical routes
 *     (`/department-head/team-metrics`, `/department-head/departments`) sit below the vendor-only
 *     boundary of the backend router, so a corrected prefix would have turned a 404 into a 403 and
 *     nothing else. The CALLS were removed from the page; the methods went with them.
 *   · `getOverview`, `getAuditSnapshot`, `getDepartmentAnalytics`, `getDataAnalysis`,
 *     `getBusinessIntelligence`, `getExecutiveReports`, `getDepartmentConfig`, `getRealTimeMetrics`
 *     target the same vendor-only boundary and had no caller on any routed surface. KNOWN RESIDUE:
 *     `stores/departmentHeadStore.js` still names `getOverview` and `getAuditSnapshot`; that store
 *     is imported only by `views/DepartmentHead/Dashboard.vue`, which no route and no module loads,
 *     so the call cannot be reached — its retirement belongs to that store's owner.
 *   · `getDepartmentMembers`, `getDepartmentIncidenceStats` and the accessible-department cache
 *     helpers had no caller at all. One of them, `getEffectiveAllowedDepartmentIds`, granted
 *     department 1 to any session whose stored role contained the word "department" when it found
 *     no membership — an invented scope.
 * Every one of those methods also swallowed its failure and returned an empty-shaped SUCCESS.
 */

import { apiClient } from '@/services/api-client'

/** A number the server actually stated: a finite number, or a numeric string. Never a default. */
const statedNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

class JefeDepartamentoService {
  constructor() {
    // Prefix-scoped como el resto de la app: el cliente canónico YA aporta la base '/api'
    // (api-client.ts fija config.baseURL = getApiBaseUrl() en el interceptor de request), así que un
    // literal que empiece por '/api/' compone '/api/api/...', que el backend no monta.
    this.membershipsURL = '/memberships'
    this.departmentHeadIncidencesURL = '/department-head/incidences'
  }

  // 👥 Membresías del usuario.
  //
  // P13-V7B: the prefix is repaired (the literal composed `/api/api/memberships/...`) because the
  // route it names exists and `GET /api/memberships/user/:user_id` admits super_admin and
  // group_manager. Its remaining callers are the two department-head Pinia stores
  // (`departmentHeadContextStore`, `departmentHeadStore`); neither is mounted by a routed page. It
  // is NOT a department-head surface: for that role the backend refuses the read, and the
  // department-head incident page stopped deriving a department from memberships (the server owns
  // that scope) and does not call this. A refusal is reported as `success: false`, never as an
  // empty membership list presented as a successful read.
  async getUserMemberships(userId) {
    try {
      const response = await apiClient.get(`${this.membershipsURL}/user/${userId}`, {
        params: { include_inactive: false }
      })
      return response.data
    } catch (error) {
      console.warn('⚠️ No se pudo obtener membresías del usuario:', error?.message)
      return { success: false, data: { memberships: [], statistics: {} }, error: error?.message }
    }
  }

  // 👤 Usuario actual según el backend (para conocer el id real creado por token de test)
  async getCurrentBackendUser() {
    try {
      const response = await apiClient.get('/auth/validate')
      // El backend devuelve { status, message, user: { id, ... } }
      return response.data?.user || response.data
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el usuario actual del backend:', error?.message)
      return null
    }
  }

  // 🚨 Incidencias del jefe de departamento. EL ALCANCE ES DEL SERVIDOR.
  //
  // `loadContext` (departmentHeadController) deriva los departamentos accesibles del actor desde
  // DepartmentMembership y trata un `departmentId` del cliente como FILTRO DE ESTRECHAMIENTO: si se
  // omite devuelve todos los departamentos del actor; si se envía uno ajeno responde 403. Por eso el
  // cliente ya NO exige resolver un departamento antes de poder leer: hacerlo convertía una lectura
  // legítima en una vista vacía cuando el productor de ese id fallaba.
  //
  // Semántica explícita del argumento (NO por `arguments.length`):
  //   null / undefined      → AUSENTE  → petición canónica sin `departmentId`
  //   entero positivo finito→ ESTRECHAMIENTO válido
  //   presente pero inválido→ FAIL CLOSED — nunca degrada a una lectura sin alcance
  //
  // P13-V7B — EL TOTAL ES DEL SERVIDOR TAMBIÉN. The reader is paginated (the backend clamps `limit`
  // to 100) and states the real size of the result in `meta.total`, a SIBLING of `data` that the
  // default interceptor discards. Without it the page counted the rows it received, published
  // "25 de 25" for a department with hundreds of incidences and offered a single page, so the rest
  // were unreachable. The request now preserves the envelope (`skipUnwrap`) and returns:
  //   data  → the rows of THIS page
  //   count → `meta.total` exactly as stated, or `null` when the server stated none. A page length
  //           is never substituted for it, and a failed read reports `null`, not zero.
  //   meta  → the server's pagination/statistics block, untouched
  async getIncidencesByDepartment(departmentId = null, params = {}) {
    try {
      const absent = departmentId === null || departmentId === undefined
      let cleanId = null
      if (!absent) {
        const n = Number(departmentId)
        if (!Number.isInteger(n) || n <= 0) {
          console.warn('[DeptHead][Incidences] ID inválido, abortando fetch:', departmentId)
          return { success: false, data: [], count: null, error: 'invalid_department_id' }
        }
        cleanId = n
      }
      const effectiveParams = { ...(params && typeof params === 'object' ? params : {}) }
      if (cleanId !== null) effectiveParams.departmentId = cleanId

      const config = { skipUnwrap: true }
      // Evitar añadir '?' vacío: solo pasar params si tienen claves
      if (Object.keys(effectiveParams).length) config.params = effectiveParams

      const response = await apiClient.get(this.departmentHeadIncidencesURL, config)
      // Si viene marcado forbidden desde api-client (status 403) lo propagamos tal cual
      if (response?.status === 403 || response?.data?.forbidden) {
        return { success: false, data: [], count: null, forbidden: true, reason: response?.data?.reason }
      }

      const payload = response?.data
      const rows = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : null)
      if (rows === null) {
        console.warn('[DeptHead][Incidences] respuesta sin filas reconocibles')
        return { success: false, data: [], count: null, error: 'invalid_envelope' }
      }
      const meta = payload && !Array.isArray(payload) && payload.meta && typeof payload.meta === 'object' ? payload.meta : null
      return { success: true, data: rows, count: statedNumber(meta?.total), meta }
    } catch (error) {
      // Si se lanzó exception (solo para errores inesperados ahora)
      if (error?.response?.status === 403) {
        return { success: false, data: [], count: null, forbidden: true }
      }
      console.warn('⚠️ Error inesperado al obtener incidencias por departamento:', error?.message)
      return { success: false, data: [], count: null, error: error?.message }
    }
  }
}

// Instancia única exportada sin monkey patch adicional (la lógica de acceso ahora reside en el store contextual)
export default new JefeDepartamentoService()
